import { Link } from "react-router-dom";
import { Card, CardContent, StatusBadge, Timestamp } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { useEmailTemplates } from "@/hooks/emailTemplates/useEmailTemplates";
import type { EmailTemplateGroup, EmailTemplateSummary } from "@/types/emailTemplates";

/**
 * Admin "Email Templates" — every outbound email the platform sends, with enough
 * override state to tell at a glance whether anyone has touched it.
 *
 * Grouped by audience with COUNTERPARTY FIRST, because that is the group whose
 * wording leaves Phoenix. A group with no templates still renders, labelled: an
 * empty category and a missing category are different facts, and while the
 * conversion lands module by module the difference is the useful one.
 */
export function EmailTemplatesPage() {
  const query = useEmailTemplates();
  const groups = query.data ?? [];

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Admin"
        title="Email Templates"
        description="The copy of every outbound email. Open one to edit its subject and body blocks; a saved edit is a draft and does not send until it is published. Every publish is versioned and can be rolled back."
      />

      {groups.map((group) => (
        <GroupSection
          key={group.audience}
          group={group}
          loading={query.isLoading}
          error={query.isError ? "Could not load the email templates." : null}
        />
      ))}

      {!query.isLoading && groups.length === 0 && (
        <Card>
          <CardContent>
            <p className="text-small text-gray-mid">No email templates are catalogued yet.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function GroupSection({
  group,
  loading,
  error,
}: {
  group: EmailTemplateGroup;
  loading: boolean;
  error: string | null;
}) {
  const columns: Column<EmailTemplateSummary>[] = [
    {
      key: "email",
      header: "Email",
      render: (t) => (
        <div className="flex flex-col gap-space-1">
          <Link to={`/admin/email-templates/${encodeURIComponent(t.key)}`} className="font-mono text-caption text-gold-shadow hover:underline">
            {t.key}
          </Link>
          <span className="text-caption text-gray-mid">{t.description}</span>
        </div>
      ),
    },
    {
      key: "trigger",
      header: "Sent when",
      render: (t) => <span className="text-caption text-gray-mid">{t.trigger}</span>,
      mobileHidden: true,
    },
    {
      key: "blocks",
      header: "Blocks",
      render: (t) => t.blockCount,
      mobileHidden: true,
    },
    {
      key: "live",
      header: "Live overrides",
      // The count that changes what sends. Kept visually distinct from the draft
      // column beside it, because conflating the two would tell an admin their
      // edit is going out when it is not.
      render: (t) =>
        t.customizedBlockCount > 0 ? (
          <StatusBadge tone="info">{t.customizedBlockCount} customised</StatusBadge>
        ) : (
          <span className="text-gray-mid">Code default</span>
        ),
    },
    {
      key: "drafts",
      header: "Pending",
      render: (t) =>
        t.draftBlockCount > 0 ? (
          <StatusBadge tone="warn">{t.draftBlockCount} unpublished</StatusBadge>
        ) : (
          <span className="text-gray-mid">—</span>
        ),
    },
    {
      key: "version",
      header: "Version",
      render: (t) =>
        t.currentVersionNumber == null ? (
          <span className="text-gray-mid">—</span>
        ) : (
          <span>v{t.currentVersionNumber}</span>
        ),
      mobileHidden: true,
    },
    {
      key: "published",
      header: "Last published",
      render: (t) =>
        t.lastPublishedAtUtc ? (
          <Timestamp value={t.lastPublishedAtUtc} />
        ) : (
          <span className="text-gray-mid">Never</span>
        ),
      mobileHidden: true,
    },
  ];

  return (
    <section className="flex flex-col gap-space-2">
      <h2 className="text-label uppercase tracking-wide text-gray-mid">{group.audienceLabel}</h2>
      <DataTable
        columns={columns}
        rows={group.templates}
        rowKey={(t) => t.key}
        loading={loading}
        error={error}
        emptyMessage="No emails in this group are catalogued yet."
      />
    </section>
  );
}
